import React, { useState } from 'react'
import { Box, Checkbox, FormControlLabel } from '@material-ui/core'
import { ApolloError } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import { Dropzone } from '../form/Dropzone'
import { Dialog } from '../dialog/Dialog'
import { Loader } from '../common/Loader'
import { useNotifications } from '../../notifications/use-notifications'
import { ProjectFragment } from '../../generated/graphql'

type Props = {
  isOpen: boolean
  isLoading: boolean
  onClose: () => void
  onCreateCollections: (
    file: File,
    shouldOverwrite: boolean,
    project?: ProjectFragment,
  ) => Promise<void>
}

export function ImportHourSeriesDialog({
  isOpen,
  isLoading,
  onClose,
  onCreateCollections,
}: Props): React.ReactElement {
  const [shouldOverwrite, setShouldOverwrite] = useState(true)
  const { t } = useTranslation()
  const { enqueueSuccess, enqueueError } = useNotifications()

  const onImport = async (hourSeriesFile: File) => {
    try {
      await onCreateCollections(hourSeriesFile, shouldOverwrite)

      enqueueSuccess(t('projectHourSeries.importSuccess'))
    } catch (error) {
      if (!(error instanceof ApolloError)) {
        throw error
      }

      const graphQlError = error.graphQLErrors[0]

      switch (graphQlError.extensions?.code) {
        case 'INVALID_VALUE':
          enqueueError(
            t(
              'projectHourSeries.importFailed.invalidValue',
              graphQlError.extensions,
            ),
          )
          break

        case 'INVALID_NUMBER_OF_VALUES':
          enqueueError(
            t(
              'projectHourSeries.importFailed.invalidNumberOfValues',
              graphQlError.extensions,
            ),
          )
          break

        case 'INVALID_NUMBER_OF_BUILDING_TEMPLATES':
          enqueueError(
            t(
              'projectHourSeries.importFailed.invalidNumberOfBuildingTemplates',
              graphQlError.extensions,
            ),
          )
          break

        case 'INVALID_HOUR_SERIES_TYPE':
          enqueueError(
            t(
              'projectHourSeries.importFailed.invalidHourSeriesType',
              graphQlError.extensions,
            ),
          )
          break

        default:
          enqueueError(t('projectHourSeries.importFailed.other'))
          break
      }
    }

    onClose()
  }

  const content = isLoading ? (
    <Loader height={545} />
  ) : (
    <>
      <Dropzone
        id="hour-series-file"
        label={t('projectHourSeries.importDialog.instructions')}
        options={{
          maxFiles: 1,
          accept: [
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            '.csv',
          ],
        }}
        onFileDrop={(files) => onImport(files[0])}
      />

      <Box display="flex" justifyContent="center">
        <FormControlLabel
          control={
            <Checkbox
              checked={shouldOverwrite}
              onChange={() => setShouldOverwrite((value) => !value)}
              name="shouldOverwrite"
              color="primary"
            />
          }
          label={t('projectHourSeries.importDialog.shouldOverwrite')}
        />
      </Box>
    </>
  )

  return (
    <Dialog
      title={t('projectHourSeries.importDialog.title')}
      open={isOpen}
      onClose={onClose}
    >
      {content}
    </Dialog>
  )
}
